import {
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { ONE_MONTH_RANGE } from "../constants/core-constants";
import { DEFAULT_TIME } from "../constants/input-entries-constants";
import {
  FIELD_GRID_BLANK_VALUES,
  FIELD_GRID_EMPTY_VALUE
} from "../core/et3-field-grid/field-grid-constants";

import getCorePopUpStore from "../stores/core/core-popup-store";

const et3ReadableDate = (ms: number) => {
  let days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const daysms = ms % (24 * 60 * 60 * 1000);
  let hours = Math.floor(daysms / (60 * 60 * 1000));
  const hoursms = ms % (60 * 60 * 1000);
  let minutes = Math.floor(hoursms / (60 * 1000));
  const minutesms = ms % (60 * 1000);
  let sec = Math.floor(minutesms / 1000);
  let dayToShow = `${days}`;
  let hoursToShow = `${hours}`;
  let minutesToShow = `${minutes}`;
  let secondsToShow = `${sec}`;
  if (days < 10) {
    dayToShow = `0${days}`;
  }
  if (hours < 10) {
    hoursToShow = `0${hours}`;
  }
  if (minutes < 10) {
    minutesToShow = `0${minutes}`;
  }
  if (sec < 10) {
    secondsToShow = `0${sec}`;
  }
  return (
    dayToShow + ":" + hoursToShow + ":" + minutesToShow + ":" + secondsToShow
  );
};

export const isMissingServerDate = (date: string) => {
  const trimmedDate = reduce(() => date.trim(), "");
  return (
    !isDefinedString(trimmedDate) ||
    FIELD_GRID_BLANK_VALUES.indexOf(trimmedDate) > -1 ||
    trimmedDate === DEFAULT_TIME
  );
};

export const getFirstKnownServerDate = (dates: string[]) => {
  return reduce(
    () => dates.filter((date) => !isMissingServerDate(date))[0],
    FIELD_GRID_EMPTY_VALUE
  );
};

export const parseServerDate = (date: string) => {
  return date.toLocaleString().replace("Z", " ").replace("T", " ");
};
export const getDaysDifference = ({
  dateFrom,
  dateTo
}: {
  dateFrom: Date;
  dateTo: Date;
}) => {
  const daysDifferenceInTime = dateTo.getTime() - dateFrom.getTime();
  const daysDifference = daysDifferenceInTime / (1000 * 3600 * 24);
  return daysDifference;
};

export const getHoursDifference = ({
  dateFrom,
  dateTo
}: {
  dateFrom: Date;
  dateTo: Date;
}) => {
  const lastLoginDate = dateFrom.getTime();
  const nowDate = dateTo.getTime();
  const dateDifference = nowDate - lastLoginDate;
  const dateDifferenceInSeconds = dateDifference / 1000;
  const dateDifferenceInMinutes = dateDifferenceInSeconds / 60;
  const dateDifferenceInHours = dateDifferenceInMinutes / 60;
  return dateDifferenceInHours;
};

export const getChartDateMonthYear = (serverDate: string) => {
  const date = new Date(serverDate);
  const month = date.getMonth() + 1;
  let monthString = `${month}`;
  if (month < 10) {
    monthString = `0${month}`;
  }
  const year = date.getFullYear();
  return `${monthString}/${year}`;
};

export const isDateRangeValid = ({
  fromDate,
  toDate,
  isLimitedToOneMonth
}: {
  fromDate: Date;
  toDate: Date;
  isLimitedToOneMonth: boolean;
}) => {
  const daysDiff = getDaysDifference({
    dateFrom: fromDate,
    dateTo: toDate
  });
  if (isNullOrUndefined(fromDate) || isNullOrUndefined(toDate)) {
    return false;
  }
  if (daysDiff > ONE_MONTH_RANGE && isLimitedToOneMonth) {
    getCorePopUpStore().showAlertMessage("SELECT_SMALLER_DATE_RANGE");
    return false;
  }
  if (!isPositiveNumber(daysDiff, true)) {
    getCorePopUpStore().showAlertMessage("SELECT_CORRECT_DATE_RANGE");
    return false;
  }
  return true;
};

export const isStartDateBeforeExpiry = (startDate: Date, expiryDate: Date) => {
  if (isNullOrUndefined(startDate) || isNullOrUndefined(expiryDate)) {
    return false;
  }

  const startDateOnly = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const expiryDateOnly = new Date(
    expiryDate.getFullYear(),
    expiryDate.getMonth(),
    expiryDate.getDate()
  );

  if (startDateOnly >= expiryDateOnly) {
    getCorePopUpStore().showAlertMessage("CHECK_START_EXPIRY_DATE");
    return false;
  }
  return true;
};

export const getFormmatedDate = (date: Date) => {
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  let day = date.getDate();
  let hour = date.getHours();
  let minute = date.getMinutes();
  let second = date.getSeconds();

  let s_month = month.toString();
  let s_day = day.toString();
  let s_hour = hour.toString();
  let s_minute = minute.toString();
  let s_second = second.toString();

  if (month < 10) {
    s_month = `0${month}`;
  }
  if (day < 10) {
    s_day = `0${day}`;
  }
  if (hour < 10) {
    s_hour = `0${hour}`;
  }
  if (minute < 10) {
    s_minute = `0${minute}`;
  }
  if (second < 10) {
    s_second = `0${second}`;
  }
  return `${year}-${s_month}-${s_day} ${s_hour}:${s_minute}:${s_second}`;
};

export const getMonthAndYear = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${month} ${year}`;
};

export const getAndAddDaysToSpecifiedTime = (
  specifiedTime: string,
  daysToAdd: number
) => {
  let date = new Date(specifiedTime);
  date.setDate(date.getDate() + daysToAdd);
  return getFormmatedDate(date);
};

export const getDayMonthYearJsFormat = (initialDate: Date) => {
  const formattedDate = `${initialDate.getFullYear()}-${(
    initialDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${initialDate.getDate().toString().padStart(2, "0")}`;
  const [year, month, day] = formattedDate.split("-");
  let date = new Date();
  date.setDate(1); // Temporarily set the day to 1 to prevent month overflow when setting a shorter month (e.g., February)
  date.setFullYear(parseInt(year));
  date.setMonth(parseInt(month) - 1);
  date.setDate(parseInt(day));
  return new Date(date);
};

export const isWithinLast24Hours = (date: string) => {
  const now = Date.now();
  const target = new Date(date).getTime();
  return now - target < 24 * 60 * 60 * 1000;
};

export const createDateFromSpecificDateString = (
  stringifiedDate: string
): Date => {
  if (!isDefinedString(stringifiedDate)) {
    return new Date();
  }
  return new Date(stringifiedDate);
};

export const getDefaultDate = (): Date => {
  return new Date(DEFAULT_TIME);
};

export const getReadableDateFromUnixMilliseconds = (
  milliseconds: number
): string => {
  if (!isPositiveNumber(milliseconds)) {
    return "";
  }
  return reduce(() => new Date(milliseconds).toLocaleString("en-GB"), "");
};

export default et3ReadableDate;
